import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { I18nextProvider } from "react-i18next";

import i18n, {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@/lib/i18n/config";

const STORAGE_KEY = "braise-language";
const DEFAULT_LANGUAGE: SupportedLanguage = "en";

type LanguageContextValue = {
  language: SupportedLanguage;
  resolvedLanguage: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  availableLanguages: SupportedLanguage[];
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const normalizeLanguage = (value: string | undefined | null): SupportedLanguage | null => {
  if (!value) {
    return null;
  }
  const lower = value.toLowerCase();
  const exactMatch = (SUPPORTED_LANGUAGES as readonly string[]).find((language) => language === lower);
  if (exactMatch) {
    return exactMatch as SupportedLanguage;
  }
  const shortCode = lower.slice(0, 2);
  if ((SUPPORTED_LANGUAGES as readonly string[]).includes(shortCode)) {
    return shortCode as SupportedLanguage;
  }
  return null;
};

const readStoredLanguage = (): SupportedLanguage | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return normalizeLanguage(window.localStorage.getItem(STORAGE_KEY));
};

const detectBrowserLanguage = (): SupportedLanguage | null => {
  if (typeof navigator === "undefined") {
    return null;
  }
  const sequence = [navigator.language, ...(navigator.languages ?? [])];
  for (const candidate of sequence) {
    const normalized = normalizeLanguage(candidate);
    if (normalized) {
      return normalized;
    }
  }
  return null;
};

const resolveInitialLanguage = (): SupportedLanguage => {
  return readStoredLanguage() ?? detectBrowserLanguage() ?? normalizeLanguage(i18n.language) ?? DEFAULT_LANGUAGE;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(resolveInitialLanguage);
  const [resolvedLanguage, setResolvedLanguage] = useState<SupportedLanguage>(() => {
    return normalizeLanguage(i18n.resolvedLanguage) ?? resolveInitialLanguage();
  });

  useEffect(() => {
    if (language === normalizeLanguage(i18n.language)) {
      return;
    }
    i18n.changeLanguage(language).catch((error) => {
      if (import.meta.env.DEV) {
        console.error("Failed to change language", error);
      }
    });
  }, [language]);

  useEffect(() => {
    const handleLanguageChanged = (nextLanguage: string) => {
      const normalized = normalizeLanguage(nextLanguage);
      if (normalized) {
        setResolvedLanguage(normalized);
      }
    };
    i18n.on("languageChanged", handleLanguageChanged);
    handleLanguageChanged(i18n.resolvedLanguage);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [setResolvedLanguage]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.documentElement.lang = resolvedLanguage;
  }, [resolvedLanguage]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const setLanguage = useCallback((nextLanguage: SupportedLanguage) => {
    setLanguageState((current) => (current === nextLanguage ? current : nextLanguage));
    setResolvedLanguage((current) => (current === nextLanguage ? current : nextLanguage));
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      resolvedLanguage,
      setLanguage,
      availableLanguages: [...SUPPORTED_LANGUAGES],
    }),
    [language, resolvedLanguage, setLanguage],
  );

  return (
    <I18nextProvider i18n={i18n}>
      <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
    </I18nextProvider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
